import axios from 'axios';
import moment from 'moment';
import { toast } from 'react-toastify';

const apiUrl = process.env.REACT_APP_SERVER_API_URL;

// Axios request interceptor to add auth token
axios.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('auth-token');
        
        // Add auth token if available
        if (token) {
            config.headers['x-auth-token'] = token;
        }
        
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

export const checkIfLoggedIn = () => {
    // console.log('auth-token: ' + localStorage.getItem("auth-token"));
    // console.log('user-id: ' + localStorage.getItem("user-id"));

    let token = localStorage.getItem("auth-token");
    if (token === null) {
        localStorage.setItem("auth-token", "");
        token = "";
    }
    return axios.post(`${apiUrl}/users/isTokenValid`, null, { headers: { "x-auth-token": localStorage.getItem("auth-token") } })
        .then((tokenResponse) => {
            if (tokenResponse.data) {
                axios.get(`${apiUrl}/users`, { headers: { "x-auth-token": token } })
                    .then((userResponse) => {
                        localStorage.setItem("user-id", userResponse.data._id);
                    })
                    .catch((error) => {
                        console.log(error);
                        if (error.response?.status === 401) {
                            handleSessionExpired();
                        }
                    });
            } else {
                // Token is invalid or expired
                handleSessionExpired();
            }
        })
        .catch((error) => {
            console.log(error);
            if (error.response?.status === 401) {
                handleSessionExpired();
            }
        });
}

// When a session expires, every in-flight request 401s and each failure calls
// handleSessionExpired — without a guard that stacks N identical toasts and
// N competing redirects. toastId dedupes the toast; the flag dedupes the
// redirect (page navigation resets it).
let sessionExpiryHandled = false;

export const handleSessionExpired = () => {
    // Clear all auth data
    localStorage.removeItem("auth-token");
    localStorage.removeItem("user-id");

    // Show session expired message (once)
    if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
        toast.error('Your session has expired. Please log in again.', {
            toastId: 'session-expired',
            position: "top-center",
            autoClose: 2000
        });
        if (!sessionExpiryHandled) {
            sessionExpiryHandled = true;
            setTimeout(() => {
                window.location.href = '/login';
            }, 2000);
        }
    }
}

// Token refresh function
export const refreshAuthToken = async () => {
    try {
        const token = localStorage.getItem("auth-token");
        if (!token) return null;

        const response = await axios.post(`${apiUrl}/users/refreshToken`, null, {
            headers: { "x-auth-token": token }
        });

        const newToken = response.data.token;
        localStorage.setItem("auth-token", newToken);
        return newToken;
    } catch (error) {
        console.log('Token refresh failed:', error);
        // Clear activity timer to stop the loop
        if (activityTimer) {
            clearTimeout(activityTimer);
            activityTimer = null;
        }
        handleSessionExpired();
        return null;
    }
};

// Activity tracking - refresh token when user is active
let activityTimer = null;

const resetActivityTimer = () => {
    if (activityTimer) {
        clearTimeout(activityTimer);
    }
    
    // Refresh token every 30 minutes of activity (half the session timeout)
    activityTimer = setTimeout(async () => {
        const token = localStorage.getItem("auth-token");
        if (token) {
            await refreshAuthToken();
        }
    }, 30 * 60 * 1000); // 30 minutes
};

// Track user activity
const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
activityEvents.forEach(event => {
    document.addEventListener(event, resetActivityTimer, true);
});

// Initialize activity timer when service loads
if (localStorage.getItem("auth-token")) {
    resetActivityTimer();
}

// Axios interceptor to handle 401 responses and refresh tokens
axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        
        // Don't try to refresh if this IS a refresh token request that failed
        if (originalRequest.url?.includes('/refreshToken')) {
            return Promise.reject(error);
        }
        
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;
            
            // Try to refresh token
            const newToken = await refreshAuthToken();
            if (newToken) {
                // Retry the original request with new token
                originalRequest.headers['x-auth-token'] = newToken;
                return axios(originalRequest);
            }
        }
        
        if (error.response?.status === 401) {
            handleSessionExpired();
        }
        return Promise.reject(error);
    }
);

// Display-only date-range formatter: "20–24 Jul 2026", "29 Jun – 3 Jul 2026",
// "29 Dec 2026 – 2 Jan 2027". Collapses the month/year when shared so ranges
// read the same way everywhere (booking rows, cart, orders, confirmation).
export const formatDateRange = (start, end) => {
    const s = moment(start);
    const e = moment(end);
    if (!s.isValid() || !e.isValid()) return '';
    if (s.isSame(e, 'day')) return s.format('D MMM YYYY');
    if (s.isSame(e, 'month') && s.isSame(e, 'year')) return `${s.format('D')}–${e.format('D MMM YYYY')}`;
    if (s.isSame(e, 'year')) return `${s.format('D MMM')} – ${e.format('D MMM YYYY')}`;
    return `${s.format('D MMM YYYY')} – ${e.format('D MMM YYYY')}`;
};

// Single-date display format used alongside formatDateRange: "5 Jul 2026"
export const formatDate = (date) => {
    const d = moment(date);
    return d.isValid() ? d.format('D MMM YYYY') : '';
};

export const formatPrice = (n) => {
    const num = Number(typeof n === 'string' ? n.replace(/[$,]/g, '') : n);
    return (isNaN(num) ? 0 : num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Effective price for a cart item, preferring the server-computed currentPrice
// (getCart annotates it from the live season). Falls back to computing from the
// item's populated season (e.g. just-added items before a cart refresh), then to
// the stored price. Mirrors the server's getSeasonEffectivePrice rule.
export const getItemEffectivePrice = (item) => {
    if (item && item.currentPrice != null && !isNaN(Number(item.currentPrice))) {
        return Number(item.currentPrice);
    }
    const season = item?.week?.season;
    if (season && season.price != null) {
        const d = season.discount;
        const active = d && d.percentage > 0 && d.endDate && new Date(d.endDate) > new Date();
        const raw = active ? season.price * (1 - d.percentage / 100) : season.price;
        return Math.round(raw * 100) / 100;
    }
    const stored = typeof item?.price === 'string' ? Number(item.price.replace(/[$,]/g, '')) : Number(item?.price);
    return isNaN(stored) ? 0 : stored;
};

export const searchAddressByPostalcode = async (fv, setFormValue) => {
    await axios.get(`${apiUrl}/postalCodes/${fv.postalCode}`)
        .then((response) => {
            fv.address = response.data.address;
            setFormValue(fv);
        })
        .catch((err) => {
            console.log(err);
            setFormValue(fv);
        });

}